import { fetchConfig } from "@theme/utilities";
import {
  CartAddInitiatedEvent,
  CartAddEvent,
  CartErrorEvent,
} from "@theme/events";

/**
 * @class Cart
 * @description A utility class providing static methods for interacting with the theme's shopping cart.
 */
export class Cart {
  /**
   * Creates a FormData instance from a plain JavaScript object.
   * This method handles boolean values by converting them to string representations ("true" or "false")
   * and skips `null` and `undefined` values.
   *
   * @static
   * @param {object} data The plain JavaScript object to convert into FormData.
   * @returns {FormData} A FormData instance populated with the object's data.
   */
  static createFormDataFromObject(data) {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      // Skip null and undefined values
      if (value === null || value === undefined) {
        return;
      }

      // If the value is a boolean, put in text attribute value
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
        return;
      }

      // Handle all other types (string, number)
      formData.append(key, value);
    });

    return formData;
  }

  /**
   * Collects cart section IDs used for section rendering.
   * @returns {string[]}
   */
  static getCartItemSectionIds() {
    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    return cartItemComponentsSectionIds;
  }

  /**
   * Asynchronously adds an item to the cart using the Shopify AJAX API.
   * It dispatches a series of custom events to signal the state of the operation
   * (`CartAddInitiatedEvent`, `CartAddEvent`, `CartErrorEvent`).
   *
   * This method also supports Shopify's section rendering by collecting section IDs
   * from `cart-items-component` elements on the page.
   *
   * @static
   * @async
   * @param {string | number} identifier A unique identifier for the component or context initiating the action.
   * @param {FormData} formData The FormData object containing cart item details (e.g., 'id', 'quantity', 'product-id').
   * @param {object} [options={}] Optional configuration and callbacks.
   * @param {(response: object) => void} [options.successCallback] A callback executed on a successful API response. Receives the JSON response.
   * @param {(errorResponse: object) => void} [options.errorCallback] A callback executed on a failed API response. Receives the JSON error response.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  static async add(
    identifier,
    formData,
    { successCallback, errorCallback } = {},
  ) {
    const variantId = formData.get("id");
    const productId = formData.get("product-id");
    const productHandle = formData.get("product-handle");
    const quantity = Number(formData.get("quantity") || 1);

    document.dispatchEvent(
      new CartAddInitiatedEvent({}, identifier, {
        source: "sdk",
        itemCount: quantity,
        productId,
      }),
    );

    const cartItemComponentsSectionIds = Cart.getCartItemSectionIds();

    if (cartItemComponentsSectionIds.length) {
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    const defaultEventObject = {
      source: "sdk",
      itemCount: quantity,
      productId,
      productHandle,
      variantId,
    };

    try {
      const res = await fetch(Theme.routes.root + "cart/add.js", {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "text/html",
        },
      });

      const response = await res.json();

      // Error handling via response
      if (response.status) {
        document.dispatchEvent(
          new CartErrorEvent(identifier, response.message),
        );

        if (errorCallback) {
          errorCallback(response);
        }

        // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
        // because our back-end still adds the max allowed amount to the cart.
        document.dispatchEvent(
          new CartAddEvent({}, identifier, {
            didError: true,
            ...defaultEventObject,
          }),
        );
      } else {
        if (successCallback) {
          successCallback(response);
        }

        if (!variantId) throw new Error("Form variant ID is required");

        document.dispatchEvent(
          new CartAddEvent({}, "theme", defaultEventObject),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Adds multiple items to the cart in a single atomic request.
   *
   * @param {string | number} identifier A unique identifier for the component or context initiating the action.
   * @param {Array<{id: string | number, quantity: number, selling_plan?: string | number, properties?: Record<string, string>}>} items
   * @param {{ productId?: string | null, productHandle?: string | null, variantId?: string | null, hasCrossSellRedirect?: boolean }} [context={}]
   * @param {(response: object) => void} [options.successCallback]
   * @param {(errorResponse: object) => void} [options.errorCallback]
   * @returns {Promise<void>}
   */
  static async addItems(
    identifier,
    items,
    context = {},
    { successCallback, errorCallback } = {},
  ) {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error("Cart.addItems requires at least one item");
    }

    const normalizedItems = items
      .filter((item) => item?.id)
      .map((item) => ({
        ...item,
        quantity: Number(item.quantity || 1),
      }));

    if (!normalizedItems.length) {
      throw new Error("Cart.addItems requires items with valid IDs");
    }

    const cartItemComponentsSectionIds = Cart.getCartItemSectionIds();
    const payload = {
      items: normalizedItems,
    };

    if (cartItemComponentsSectionIds.length) {
      payload.sections = cartItemComponentsSectionIds.join(",");
    }

    const variantId =
      context.variantId ||
      (normalizedItems[0] ? String(normalizedItems[0].id) : null);
    const quantity = normalizedItems.reduce(
      (sum, item) => sum + Number(item.quantity || 1),
      0,
    );

    const defaultEventObject = {
      source: "sdk",
      itemCount: quantity,
      productId: context.productId || null,
      productHandle: context.productHandle || null,
      variantId,
      hasCrossSellRedirect: Boolean(context.hasCrossSellRedirect),
    };

    document.dispatchEvent(new CartAddInitiatedEvent(defaultEventObject));

    const fetchCfg = fetchConfig("json", {
      body: JSON.stringify(payload),
    });

    try {
      const res = await fetch(Theme.routes.root + "cart/add.js", fetchCfg);
      const response = await res.json();

      if (response.status) {
        document.dispatchEvent(
          new CartErrorEvent(identifier, response.message),
        );

        if (errorCallback) {
          errorCallback(response);
        }

        // Batch add.js is atomic: a failure leaves the cart unchanged, so do
        // not publish cart:update. Cart.add still does for the inventory-max
        // case, where Shopify adds the allowed quantity anyway.
        return;
      }

      if (successCallback) {
        successCallback(response);
      }

      document.dispatchEvent(
        new CartAddEvent({}, "theme", {
          ...defaultEventObject,
          sections: response.sections,
        }),
      );
    } catch (error) {
      console.error(error);

      // A network or JSON failure still needs to unwind the UI. Without this
      // the add-to-cart button stays in its loading state with no message,
      // and cart:add:initiated has already been dispatched above.
      document.dispatchEvent(
        new CartErrorEvent(
          identifier,
          error instanceof Error ? error.message : String(error),
        ),
      );

      if (errorCallback) {
        errorCallback({ message: error?.message, error });
      }
    }
  }
}
